import { useState, useEffect } from 'react'

import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'

const ContactSettings = ({ contacts = [], onContactsChange }) => {
    const { auth, setAuth } = useAuth()

    const axios = useAxiosPrivate()

    const [activeTabsByContact, setActiveTabsByContact] = useState({})
    const [selectedContact, setSelectedContact] = useState(null)

    const [defaultContactSettings, setDefaultContactSettings] = useState({})
    const [defaultPolicySettings, setDefaultPolicySettings] = useState({})

    const [policySettings, setPolicySettings] = useState(auth?.currentPolicy?.policy_information?.settings)
    const [activePolicyTab, setActivePolicyTab] = useState(null)
    const policyId = auth?.currentPolicy?.policy_id

    useEffect(() => {
        getDefaultContactSettings()
        getDefaultPolicySettings()
    }, [])

    useEffect(() => {
        if (!policyId) {
            setPolicySettings({})
            return
        }

        getPolicySettings(policyId)
    }, [policyId])

    const isPlainObject = (value) => {
        return value !== null && typeof value === 'object' && !Array.isArray(value)
    }

    const getDefaultPolicySettings = async () => {
        const url = "/policy-settings/get-defaults"
        const response = await axios.get(url)

        if (response?.status !== 200) {
            console.error('Failed to fetch default policy settings:', response)
            return
        }

        setDefaultPolicySettings(response.data)
    }

    const getPolicySettings = async (currentPolicyId) => {
        try {
            const url = `/policy-settings/${currentPolicyId}`
            const response = await axios.get(url)

            if (response?.status !== 200) {
                console.error('Failed to fetch policy settings:', response)
                setPolicySettings({})
                return
            }

            setPolicySettings(isPlainObject(response.data) ? response.data : {})
        } catch (error) {
            console.error('Failed to fetch policy settings:', error)
            setPolicySettings({})
        }
    }

    const getDefaultContactSettings = async () => {
        const url = `/contact-settings/get-defaults`
        const response = await axios.get(url)

        if (response?.status !== 200) {
            console.error('Failed to fetch default contact settings:', response)
            return
        }

        setDefaultContactSettings(response.data)
    }

    const prettifyFieldName = (fieldName = '') => {
        return fieldName
            .split('_')
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
    }

    const parseContactSettings = (contactSettings) => {
        if (typeof contactSettings === 'string') {
            try {
                const parsed = JSON.parse(contactSettings)
                return isPlainObject(parsed) ? parsed : {}
            } catch {
                return {}
            }
        }

        return isPlainObject(contactSettings) ? contactSettings : {}
    }

    const getSettingsByTab = (contactSettings) => {
        const parsedContactSettings = parseContactSettings(contactSettings)

        if (isPlainObject(parsedContactSettings.settings)) {
            return parsedContactSettings.settings
        }

        return parsedContactSettings
    }

    const mergeSettingsByTab = (baseSettings = {}, overrideSettings = {}) => {
        const mergedSettings = isPlainObject(baseSettings) ? { ...baseSettings } : {}

        if (!isPlainObject(overrideSettings)) {
            return mergedSettings
        }

        Object.entries(overrideSettings).forEach(([tabName, tabSettings]) => {
            const baseTabSettings = mergedSettings[tabName]

            if (isPlainObject(baseTabSettings) && isPlainObject(tabSettings)) {
                mergedSettings[tabName] = {
                    ...baseTabSettings,
                    ...tabSettings
                }
                return
            }

            mergedSettings[tabName] = tabSettings
        })

        return mergedSettings
    }

    const getContactKey = (contact, index) => {
        return contact?.contact_id ?? contact.contact_email ?? index
    }

    const sortTabNames = (tabNames = []) => {
        const sortedTabNames = [...tabNames].sort((leftTab, rightTab) => {
            return leftTab.localeCompare(rightTab, undefined, { sensitivity: 'base' })
        })
        const generalTabIndex = sortedTabNames.findIndex((tabName) => tabName.toLowerCase() === 'general')

        if (generalTabIndex === -1) {
            return sortedTabNames
        }

        const [generalTabName] = sortedTabNames.splice(generalTabIndex, 1)
        return [generalTabName, ...sortedTabNames]
    }

    const getTabItems = (tabValue) => {
        if (!isPlainObject(tabValue)) {
            return []
        }

        return Object.entries(tabValue)
    }

    const persistContactSetting = async (contactId, tabName, fieldName, nextValue) => {
        try {
            const url = `${process.env.REACT_APP_KIS}/api/v1/contact-settings/update/${contactId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`
            const response = await axios.post(url, {
                tab: tabName,
                key: fieldName,
                value: nextValue
            })

            if (response?.status !== 200) {
                console.error('Failed to update contact setting:', response)
            }
        } catch (error) {
            console.error('Failed to update contact setting:', error)
        }
    }

    const persistPolicySetting = async (tabName, fieldName, nextValue) => {
        const policyId = auth?.currentPolicy?.policy_id

        if (!policyId) {
            return
        }

        try {
            const url = `/policy-settings/update/${policyId}`
            const response = await axios.post(url, {
                tab: tabName,
                key: fieldName,
                value: nextValue
            })

            if (response?.status !== 200) {
                console.error('Failed to update policy setting:', response)
            }
        } catch (error) {
            console.error('Failed to update policy setting:', error)
        }
    }

    const updateContactSetting = (contact, contactIndex, tabName, fieldName, nextValue) => {
        if (typeof onContactsChange !== 'function') {
            return
        }

        if (!contact?.contact_id) {
            console.error('Cannot update contact settings without a contact id.')
            return
        }

        const targetContactKey = getContactKey(contact, contactIndex)

        onContactsChange((previousContacts = []) => {
            return previousContacts.map((existingContact, existingIndex) => {
                const existingContactKey = getContactKey(existingContact, existingIndex)

                if (existingContactKey !== targetContactKey) {
                    return existingContact
                }

                const parsedContactSettings = parseContactSettings(existingContact.contact_settings)
                const currentSettingsByTab = mergeSettingsByTab(defaultContactSettings, getSettingsByTab(parsedContactSettings))
                const nextSettingsByTab = {
                    ...currentSettingsByTab,
                    [tabName]: {
                        ...(currentSettingsByTab[tabName] && typeof currentSettingsByTab[tabName] === 'object' ? currentSettingsByTab[tabName] : {}),
                        [fieldName]: nextValue
                    }
                }
                const nextContactSettingsObject = {
                    ...parsedContactSettings,
                    settings: nextSettingsByTab
                }

                void persistContactSetting(existingContact.contact_id, tabName, fieldName, nextValue)

                return {
                    ...existingContact,
                    contact_settings: typeof existingContact.contact_settings === 'string'
                        ? JSON.stringify(nextContactSettingsObject)
                        : nextContactSettingsObject
                }
            })
        })
    }

    const updatePolicySetting = (tabName, fieldName, nextValue) => {
        const parsedPolicySettings = parseContactSettings(policySettings)
        const currentSettingsByTab = mergeSettingsByTab(defaultPolicySettings, getSettingsByTab(parsedPolicySettings))
        const nextSettingsByTab = {
            ...currentSettingsByTab,
            [tabName]: {
                ...(currentSettingsByTab[tabName] && typeof currentSettingsByTab[tabName] === 'object' ? currentSettingsByTab[tabName] : {}),
                [fieldName]: nextValue
            }
        }
        const nextPolicySettings = {
            ...parsedPolicySettings,
            settings: nextSettingsByTab
        }

        setPolicySettings(nextPolicySettings)

        if (auth?.currentPolicy) {
            const policyInformation = parseContactSettings(auth.currentPolicy.policy_information)
            const nextCurrentPolicy = {
                ...auth.currentPolicy,
                policy_information: typeof auth.currentPolicy.policy_information === 'string'
                    ? JSON.stringify({ ...policyInformation, settings: nextSettingsByTab })
                    : { ...policyInformation, settings: nextSettingsByTab }
            }

            setAuth({ ...auth, currentPolicy: nextCurrentPolicy })
            localStorage.setItem('currentPolicy', JSON.stringify(nextCurrentPolicy))
        }

        void persistPolicySetting(tabName, fieldName, nextValue)
    }

    const selectedContactIndex = contacts.findIndex((contact, index) => getContactKey(contact, index) === selectedContact)
    const selectedContactRecord = selectedContactIndex >= 0 ? contacts[selectedContactIndex] : null
    const selectedContactKey = selectedContactRecord ? getContactKey(selectedContactRecord, selectedContactIndex) : null
    const selectedSettingsByTab = selectedContactRecord
        ? mergeSettingsByTab(defaultContactSettings, getSettingsByTab(selectedContactRecord.contact_settings))
        : defaultContactSettings
    const selectedTabNames = sortTabNames(
        Object.keys(selectedSettingsByTab).filter((tabName) => getTabItems(selectedSettingsByTab[tabName]).length > 0)
    )
    const selectedActiveTab = selectedContactKey && selectedTabNames.includes(activeTabsByContact[selectedContactKey])
        ? activeTabsByContact[selectedContactKey]
        : selectedTabNames[0]
    const selectedActiveTabItems = selectedActiveTab
        ? getTabItems(selectedSettingsByTab[selectedActiveTab])
        : []
    const selectedRowEndIndex = selectedContactIndex === -1
        ? -1
        : Math.min(Math.floor(selectedContactIndex / 2) * 2 + 1, contacts.length - 1)
    const selectedPolicySettingsByTab = mergeSettingsByTab(defaultPolicySettings, getSettingsByTab(policySettings))
    const selectedPolicyTabNames = sortTabNames(
        Object.keys(selectedPolicySettingsByTab).filter((tabName) => getTabItems(selectedPolicySettingsByTab[tabName]).length > 0)
    )
    const selectedActivePolicyTab = selectedPolicyTabNames.includes(activePolicyTab)
        ? activePolicyTab
        : selectedPolicyTabNames[0]
    const selectedActivePolicyTabItems = selectedActivePolicyTab
        ? getTabItems(selectedPolicySettingsByTab[selectedActivePolicyTab])
        : []

    return (
        <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800">Master OH</h3>
                {selectedPolicyTabNames.length > 0 ? (
                    <>
                        <div className="mt-3 flex flex-wrap items-end gap-2">
                            {selectedPolicyTabNames.map((tabName) => {
                                const isActive = tabName === selectedActivePolicyTab

                                return (
                                    <button
                                        key={tabName}
                                        type="button"
                                        onClick={() => setActivePolicyTab(tabName)}
                                        className={`rounded-lg px-2 py-1 text-sm shadow-md text-white ${isActive ? 'bg-[#02ADAD] hover:bg-teal-600' : 'bg-gray-400 hover:bg-teal-600'}`}
                                    >
                                        {prettifyFieldName(tabName)}
                                    </button>
                                )
                            })}
                        </div>

                        <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                            <table className="min-w-full text-sm">
                                <tbody>
                                    {selectedActivePolicyTabItems.map(([fieldName, value], itemIndex) => {
                                        const isLastItem = itemIndex === selectedActivePolicyTabItems.length - 1
                                        const rowBackgroundClass = itemIndex % 2 === 0 ? 'bg-slate-50' : 'bg-white'
                                        const isRestrictNonMasterSubmissions = fieldName === 'restrict_non_master_submissions'

                                        return (
                                            <tr
                                                key={`${selectedActivePolicyTab}-${fieldName}`}
                                                className={`${rowBackgroundClass} ${isLastItem ? '' : 'border-b border-gray-200'}`}
                                            >
                                                <td className="w-1/2 px-3 py-2 font-medium text-gray-700">
                                                    <p>{prettifyFieldName(fieldName)}</p>
                                                    {isRestrictNonMasterSubmissions ? (
                                                        <p className="mt-1 text-xs font-normal text-gray-600">When this is toggled on, non OH Master users will only be able to submit draft requests.</p>
                                                    ) : null}
                                                </td>
                                                <td className="w-1/2 px-3 py-2 text-right text-gray-700">
                                                    {typeof value === 'boolean' ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => updatePolicySetting(selectedActivePolicyTab, fieldName, !value)}
                                                            role="switch"
                                                            aria-checked={value}
                                                            aria-label={prettifyFieldName(fieldName)}
                                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${value ? 'bg-[#02ADAD]' : 'bg-gray-400'}`}
                                                        >
                                                            <span
                                                                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${value ? 'translate-x-5' : 'translate-x-1'}`}
                                                            />
                                                        </button>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={value === null || value === undefined ? '' : String(value)}
                                                            onChange={(event) => updatePolicySetting(selectedActivePolicyTab, fieldName, event.target.value)}
                                                            className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-600"
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <p className="mt-3 text-sm text-gray-500">No policy settings found.</p>
                )}
            </div>


            {/* <div className="flex flex-wrap gap-x-2 gap-y-0">
                {contacts && contacts.map((contact, index) => {
                    const contactKey = getContactKey(contact, index)
                    const isSelected = contactKey === selectedContact

                    return (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedContact((previous) => previous === contactKey ? null : contactKey)
                                }}
                                className={`w-full md:w-[calc(50%-0.25rem)] rounded-lg border px-3 py-2 text-left transition-transform duration-150 ${isSelected ? 'relative z-10 mb-[-1px] scale-x-[0.96] rounded-b-none border-b-transparent bg-white' : 'mb-2 scale-100 border-gray-200 bg-white hover:border-gray-400'}`}
                            >
                                <p className="text-sm font-medium text-gray-800">{contact.contact_name}</p>
                                <p className="text-xs text-gray-600">{contact.contact_email}</p>
                            </button>

                            {index === selectedRowEndIndex && selectedContactRecord ? (
                                <div key={`details-${selectedContactKey}`} className="w-full">
                                    {selectedTabNames.length > 0 ? (
                                        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white p-3 mb-2 z-[1000] shadow-sm">
                                            <h3 className="mb-2 text-lg font-semibold text-gray-800">Contact Settings</h3>
                                            <div className="mb-3 ml-1 flex flex-wrap items-end gap-2">
                                                {selectedTabNames.map((tabName) => {
                                                    const isActive = tabName === selectedActiveTab

                                                    return (
                                                        <button
                                                            key={`${selectedContactKey}-${tabName}`}
                                                            type="button"
                                                            onClick={() => {
                                                                setActiveTabsByContact((previous) => ({
                                                                    ...previous,
                                                                    [selectedContactKey]: tabName
                                                                }))
                                                            }}
                                                            className={`rounded-lg px-2 py-1 text-sm shadow-md text-white ${isActive ? 'bg-[#02ADAD] hover:bg-teal-600' : 'bg-gray-400 hover:bg-teal-600'}`}
                                                        >
                                                            {prettifyFieldName(tabName)}
                                                        </button>
                                                    )
                                                })}
                                            </div>

                                            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                                                <table className="min-w-full text-sm">
                                                    <tbody>
                                                        {selectedActiveTabItems.length > 0 ? (
                                                            selectedActiveTabItems.map(([fieldName, value], itemIndex) => {
                                                                const isLastItem = itemIndex === selectedActiveTabItems.length - 1
                                                                const rowBackgroundClass = itemIndex % 2 === 0 ? 'bg-slate-50' : 'bg-white'

                                                                return (
                                                                    <tr
                                                                        key={`${selectedContactKey}-${selectedActiveTab}-${fieldName}`}
                                                                        className={`${rowBackgroundClass} ${isLastItem ? '' : 'border-b border-gray-200'}`}
                                                                    >
                                                                        <td className="w-1/2 border-r border-gray-200 px-3 py-2 font-medium text-gray-700">{prettifyFieldName(fieldName)}</td>
                                                                        <td className="w-1/2 px-3 py-2 text-gray-700">
                                                                            {typeof value === 'boolean' ? (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => updateContactSetting(selectedContactRecord, selectedContactIndex, selectedActiveTab, fieldName, !value)}
                                                                                    role="switch"
                                                                                    aria-checked={value}
                                                                                    aria-label={prettifyFieldName(fieldName)}
                                                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${value ? 'bg-[#02ADAD]' : 'bg-gray-400'}`}
                                                                                >
                                                                                    <span
                                                                                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${value ? 'translate-x-5' : 'translate-x-1'}`}
                                                                                    />
                                                                                </button>
                                                                            ) : (
                                                                                <input
                                                                                    type="text"
                                                                                    value={value === null || value === undefined ? '' : String(value)}
                                                                                    onChange={(event) => updateContactSetting(selectedContactRecord, selectedContactIndex, selectedActiveTab, fieldName, event.target.value)}
                                                                                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-600"
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })
                                                        ) : (
                                                            <tr>
                                                                <td className="px-3 py-2 text-gray-600" colSpan={2}>No settings found in this tab.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mt-1 rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-500">No contact settings found.</div>
                                    )}
                                </div>
                            ) : null}
                        </>
                    )
                })}
            </div> */}

            {/* {contacts.length === 0 ? (
                <p className="text-sm text-gray-500">No contacts found.</p>
            ) : null} */}
        </div>
    )
}

export default ContactSettings
